import type { AnalysisState, VisualizationSpec } from '@aibi/contracts';

/**
 * Elimina del estado las referencias a claves de campo que ya no existen (orden,
 * subtotales, comparacion y visualizacion). Mantiene coherente el estado tras quitar
 * campos; la UI recalcula despues los encodings segun el tipo de visualizacion.
 */
export function pruneReferences(state: AnalysisState): AnalysisState {
  const dimensionKeys = new Set(state.dimensions.map((d) => d.key));
  const measureKeys = new Set(state.measures.map((m) => m.key));
  const has = (key: string): boolean => dimensionKeys.has(key) || measureKeys.has(key);
  const viz = state.visualization;

  const encodings: VisualizationSpec['encodings'] = {};
  for (const [channel, keys] of Object.entries(viz.encodings) as [
    keyof VisualizationSpec['encodings'],
    string[] | undefined,
  ][]) {
    const kept = (keys ?? []).filter(has);
    if (kept.length > 0) encodings[channel] = kept;
  }
  const { seriesKey } = viz.grouping;
  const map = viz.map;
  const visualization: VisualizationSpec = {
    ...viz,
    encodings,
    grouping: {
      stacking: viz.grouping.stacking,
      ...(seriesKey !== undefined && has(seriesKey) ? { seriesKey } : {}),
    },
    sorting: viz.sorting.filter((sort) => has(sort.key)),
    formatting: {
      ...viz.formatting,
      fields: Object.fromEntries(Object.entries(viz.formatting.fields).filter(([key]) => has(key))),
      conditional: viz.formatting.conditional.filter((rule) => has(rule.key)),
    },
    tooltip: { ...viz.tooltip, fields: viz.tooltip.fields.filter(has) },
  };
  if (map) {
    const { geoKey, latitudeKey, longitudeKey, ...rest } = map;
    visualization.map = {
      ...rest,
      ...(geoKey !== undefined && has(geoKey) ? { geoKey } : {}),
      ...(latitudeKey !== undefined && has(latitudeKey) ? { latitudeKey } : {}),
      ...(longitudeKey !== undefined && has(longitudeKey) ? { longitudeKey } : {}),
    };
  }

  return {
    ...state,
    sorting: state.sorting.filter((sort) => has(sort.key)),
    grouping: {
      ...state.grouping,
      rollupKeys: state.grouping.rollupKeys.filter((key) => dimensionKeys.has(key)),
    },
    comparison: state.comparison
      ? {
          ...state.comparison,
          measureKeys: state.comparison.measureKeys.filter((key) => measureKeys.has(key)),
        }
      : null,
    visualization,
  };
}
